/* ==========================================================================
   Sparvya — reviews.js
   Free, no-app review system:
     • Photos upload from the browser to Cloudinary (unsigned) or ImgBB
     • Review data POSTs to a Google Apps Script Web App, which appends a row
       to a Google Sheet and returns JSON
     • Approved reviews are fetched with GET and rendered client-side
   Setup and the Apps Script code are in REVIEWS-SETUP.md.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-reviews]');
  if (!root) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var cfg = {
    endpoint:   root.dataset.endpoint || '',
    product:    root.dataset.product || '',
    title:      root.dataset.productTitle || '',
    host:       root.dataset.imageHost || 'cloudinary',
    cloud:      root.dataset.cloudinaryCloud || '',
    preset:     root.dataset.cloudinaryPreset || '',
    imgbbKey:   root.dataset.imgbbKey || ''
  };

  var els = {
    summary:  root.querySelector('[data-reviews-summary]'),
    loading:  root.querySelector('[data-reviews-loading]'),
    list:     root.querySelector('[data-reviews-list]'),
    empty:    root.querySelector('[data-reviews-empty]'),
    avg:      root.querySelector('[data-avg]'),
    avgStars: root.querySelector('[data-avg-stars]'),
    count:    root.querySelector('[data-count]'),
    form:     root.querySelector('[data-review-form]'),
    toggle:   root.querySelector('[data-review-toggle]'),
    status:   root.querySelector('[data-review-status]'),
    submit:   root.querySelector('[data-review-submit]'),
    photo:    root.querySelector('[data-review-photo]'),
    preview:  root.querySelector('[data-review-preview]'),
    ratingIn: root.querySelector('[data-rating-value]')
  };

  var formStart = Date.now();

  /* ---------- helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function starsHtml(rating) {
    var filled = Math.round(rating);
    var out = '<span class="stars" role="img" aria-label="' + rating + ' out of 5 stars">';
    var path = '<path d="M12 2l2.9 6.3 6.8.6-5.1 4.5 1.5 6.7L12 17.8 5.9 20.6l1.5-6.7L2.3 8.9l6.8-.6z"/>';
    for (var i = 1; i <= 5; i++) {
      out += '<svg class="' + (i <= filled ? 'star-on' : 'star-off') +
             '" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' + path + '</svg>';
    }
    return out + '</span>';
  }

  function fmtDate(value) {
    if (!value) return '';
    var d = new Date(value);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  /* ---------- render ---------- */

  function render(reviews) {
    if (els.loading) els.loading.hidden = true;

    if (!reviews || !reviews.length) {
      if (els.empty) els.empty.hidden = false;
      return;
    }

    // Summary
    var total = reviews.length;
    var sum = 0, buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach(function (r) {
      var rt = Math.max(1, Math.min(5, parseInt(r.rating, 10) || 5));
      sum += rt; buckets[rt]++;
    });
    var avg = sum / total;

    if (els.avg) els.avg.textContent = avg.toFixed(1);
    if (els.avgStars) els.avgStars.innerHTML = starsHtml(avg);
    if (els.count) els.count.textContent = total;

    // Unhide the summary FIRST so the bars are laid out and visible, THEN size the
    // fills on the next frame — otherwise the width is set on a display:none element
    // and never paints (that's why the bars looked empty).
    if (els.summary) els.summary.hidden = false;

    for (var s = 1; s <= 5; s++) {
      var fill = root.querySelector('[data-bar="' + s + '"]');
      var cnt = root.querySelector('[data-bar-count="' + s + '"]');
      var pct = total ? Math.round((buckets[s] / total) * 100) : 0;
      if (cnt) cnt.textContent = buckets[s];
      if (fill) {
        fill.classList.toggle('bar__fill--has', buckets[s] > 0); // min-width sliver for any non-zero
        (function (el, p) {
          requestAnimationFrame(function () { el.style.width = p + '%'; });
        })(fill, pct);
      }
    }

    // Update the inline rating near the product title, if present
    var inline = document.querySelector('[data-rating-inline]');
    if (inline) {
      var istars = inline.querySelector('[data-inline-stars]');
      var icount = inline.querySelector('[data-inline-count]');
      if (istars) istars.innerHTML = starsHtml(avg);
      if (icount) icount.textContent = total + (total === 1 ? ' review' : ' reviews');
      inline.hidden = false;
    }

    // Cards
    var html = reviews.map(function (r) {
      var photo = r.photo ? '<div class="rev__photo"><img src="' + esc(r.photo) +
        '" alt="Photo from ' + esc(r.name) + '" loading="lazy"></div>' : '';
      var verified = String(r.verified).toLowerCase() === 'true' || r.verified === true
        ? '<span class="rev__verified">✓ Verified</span>' : '';
      var heading = r.heading ? '<div class="rev__heading">' + esc(r.heading) + '</div>' : '';
      var date = fmtDate(r.date);
      return '<article class="rev">' +
        '<div class="rev__top">' + starsHtml(r.rating) +
          (date ? '<span class="rev__date">' + esc(date) + '</span>' : '') + '</div>' +
        photo + heading +
        '<p class="rev__body">' + esc(r.body) + '</p>' +
        '<div class="rev__by"><span class="rev__name">' + esc(r.name) + '</span>' + verified + '</div>' +
      '</article>';
    }).join('');

    els.list.innerHTML = html;
    els.list.hidden = false;
  }

  /* ---------- fetch approved reviews ---------- */

  function load() {
    if (!cfg.endpoint) {
      // Backend not wired yet — show empty state, don't error.
      if (els.loading) els.loading.hidden = true;
      if (els.empty) els.empty.hidden = false;
      return;
    }
    var url = cfg.endpoint + (cfg.endpoint.indexOf('?') > -1 ? '&' : '?') +
              'product=' + encodeURIComponent(cfg.product);
    fetch(url, { method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (data) { render(data.reviews || data || []); })
      .catch(function () {
        if (els.loading) els.loading.hidden = true;
        if (els.empty) els.empty.hidden = false;
      });
  }

  /* ---------- image upload ---------- */

  function uploadImage(file) {
    if (!file) return Promise.resolve('');

    if (cfg.host === 'imgbb' && cfg.imgbbKey) {
      var fd = new FormData();
      fd.append('image', file);
      return fetch('https://api.imgbb.com/1/upload?key=' + encodeURIComponent(cfg.imgbbKey), {
        method: 'POST', body: fd
      }).then(function (r) { return r.json(); })
        .then(function (d) { return (d.data && d.data.url) || ''; });
    }

    if (cfg.cloud && cfg.preset) {
      var cd = new FormData();
      cd.append('file', file);
      cd.append('upload_preset', cfg.preset);
      return fetch('https://api.cloudinary.com/v1_1/' + cfg.cloud + '/image/upload', {
        method: 'POST', body: cd
      }).then(function (r) { return r.json(); })
        .then(function (d) { return d.secure_url || ''; });
    }

    return Promise.resolve(''); // no host configured — post the review without a photo
  }

  /* ---------- submit ---------- */

  function setStatus(msg, kind) {
    if (!els.status) return;
    els.status.textContent = msg;
    els.status.className = 'rev-form__status' + (kind ? ' ' + kind : '');
  }

  function submit(e) {
    e.preventDefault();
    var form = els.form;

    // Honeypot + dwell-time bot guards
    if (form.website && form.website.value) return;
    if (Date.now() - formStart < 2500) { setStatus('Please take a moment before posting.', 'err'); return; }

    var rating = parseInt(els.ratingIn.value, 10);
    if (!rating) { setStatus('Please choose a star rating.', 'err'); return; }
    if (!form.name.value.trim() || !form.body.value.trim()) {
      setStatus('Please add your name and a few words.', 'err'); return;
    }
    if (!cfg.endpoint) { setStatus('Reviews aren’t set up yet. Please try again later.', 'err'); return; }

    els.submit.disabled = true;
    setStatus('Uploading…', '');

    var file = els.photo && els.photo.files ? els.photo.files[0] : null;
    if (file && file.size > 8 * 1024 * 1024) {
      setStatus('That photo is over 8 MB — please choose a smaller one.', 'err');
      els.submit.disabled = false; return;
    }

    uploadImage(file).then(function (photoUrl) {
      var payload = {
        product: cfg.product,
        product_title: cfg.title,
        rating: rating,
        name: form.name.value.trim(),
        heading: form.heading.value.trim(),
        body: form.body.value.trim(),
        photo: photoUrl
      };
      // text/plain avoids a CORS preflight against Apps Script
      return fetch(cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      });
    }).then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function () {
        setStatus('Thank you! Your review will appear once we’ve approved it.', 'ok');
        form.reset();
        if (els.preview) els.preview.hidden = true;
        setStars(0);
      })
      .catch(function () {
        setStatus('Something went wrong. Please try again, or send it to us on WhatsApp.', 'err');
      })
      .then(function () { els.submit.disabled = false; });
  }

  /* ---------- star picker ---------- */

  var starBtns = root.querySelectorAll('[data-starpick] button');

  function setStars(n) {
    if (els.ratingIn) els.ratingIn.value = n || '';
    starBtns.forEach(function (b) {
      b.classList.toggle('on', parseInt(b.dataset.star, 10) <= n);
      b.classList.remove('hover');
    });
  }

  starBtns.forEach(function (b) {
    var val = parseInt(b.dataset.star, 10);
    b.addEventListener('click', function () { setStars(val); });
    b.addEventListener('mouseenter', function () {
      starBtns.forEach(function (o) { o.classList.toggle('hover', parseInt(o.dataset.star, 10) <= val); });
    });
    b.addEventListener('mouseleave', function () {
      starBtns.forEach(function (o) { o.classList.remove('hover'); });
    });
  });

  /* ---------- form toggle + photo preview ---------- */

  if (els.toggle && els.form) {
    els.toggle.addEventListener('click', function () {
      var show = els.form.hidden;
      els.form.hidden = !show;
      els.toggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      if (show) els.form.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    });
  }

  if (els.photo && els.preview) {
    els.photo.addEventListener('change', function () {
      var f = els.photo.files && els.photo.files[0];
      if (f) { els.preview.src = URL.createObjectURL(f); els.preview.hidden = false; }
      else { els.preview.hidden = true; }
    });
  }

  if (els.form) els.form.addEventListener('submit', submit);

  /* ---------- inline rating scrolls to reviews ---------- */

  var inlineBtn = document.querySelector('[data-rating-inline]');
  if (inlineBtn) {
    inlineBtn.addEventListener('click', function () {
      root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    });
  }

  load();
})();
